// src/data/supportPrograms.ts
//
// 2026년 정부지원금 데이터
// 출처: 보건복지부 2026년 기준 중위소득 고시, 고용노동부 2026년 적용 최저임금 고시,
//       국세청 근로·자녀장려금 안내, 국토교통부 주거급여 사업안내, 한국장학재단,
//       기획재정부 2027년 예산안(2026.09.01 발표) 등
// 최종 확인일: 2026-09-02
//
import { SupportProgram } from '../types';

const TODAY = '2026-09-02';

export const supportPrograms: SupportProgram[] = [
    /* ─────────────────────── 근로자·일자리 ─────────────────────── */
    {
        id: 1,
        slug: 'geunro-jangryeogeum',
        category: 'worker',
        title: '근로장려금',
        summary: '일은 하지만 소득이 적은 가구에 최대 330만원을 현금으로 지급합니다.',
        description:
            '근로장려금(EITC)은 근로·사업·종교인 소득이 있으나 소득이 적어 생활이 어려운 가구의 근로를 장려하고 실질소득을 지원하기 위해 국세청이 지급하는 근로연계형 소득지원 제도입니다. 세금을 내지 않아도 신청만 하면 현금으로 돌려받을 수 있어 2026년 현재 신청자가 가장 많은 정부지원금 중 하나입니다.',
        eligibility: '2025년 총소득이 가구유형별 기준 미만이고 재산 합계 2억 4천만원 미만인 가구',
        amount: '단독가구 최대 165만원 · 홑벌이가구 최대 285만원 · 맞벌이가구 최대 330만원',
        applicationPeriod: '정기신청 5월 1일~31일 · 반기신청 3월(하반기분) / 9월(상반기분)',
        applicationMethod: '홈택스·손택스 앱, ARS(1544-9944), 세무서 방문',
        agency: '국세청',
        requirements: [
            '총소득 기준: 단독가구 2,200만원 · 홑벌이 3,200만원 · 맞벌이 4,400만원 미만',
            '2025년 6월 1일 기준 가구원 재산 합계액 2억 4천만원 미만',
            '재산이 1억 7천만원 이상이면 산정액의 50%만 지급',
            '대한민국 국적자(또는 국적자와 혼인한 자)',
            '전문직 사업자, 다른 거주자의 부양자녀인 경우는 제외',
        ],
        documents: ['국세청 신청 안내문', '본인 명의 계좌', '신분증', '(해당 시) 소득·재산 증빙자료'],
        steps: [
            '국세청이 보낸 신청 안내문(카카오톡·문자·우편) 확인',
            '홈택스 접속 또는 ARS 1544-9944 전화',
            '주민등록번호와 개별인증번호 입력 후 신청 내용 확인',
            '환급받을 계좌 입력 후 신청 완료',
            '심사를 거쳐 정기신청분은 8월 말까지 지급',
        ],
        faq: [
            {
                q: '근로장려금과 자녀장려금을 같이 받을 수 있나요?',
                a: '네, 요건을 모두 충족하면 두 장려금을 동시에 받을 수 있습니다. 한 번의 신청으로 함께 심사됩니다.',
            },
            {
                q: '안내문을 못 받았는데 신청할 수 있나요?',
                a: '안내문은 요건 충족이 예상되는 가구에 보내는 것이므로, 받지 못했더라도 요건에 해당하면 홈택스에서 직접 신청할 수 있습니다.',
            },
            {
                q: '신청 기한을 놓치면 못 받나요?',
                a: '정기신청 기한이 지나도 6월 1일부터 11월 30일까지 기한 후 신청이 가능합니다. 다만 산정액의 95%만 지급됩니다.',
            },
        ],
        tags: ['근로장려금', 'EITC', '국세청', '현금지원', '저소득'],
        changes2026:
            '반기신청 제도가 정착되어 3월과 9월에도 신청할 수 있으며, 재산 기준 2억 4천만원 미만이 유지됩니다.',
        outlook2027:
            '2027년 예산안에서 양극화 대응 예산이 117조 1천억원(전년 대비 36.6% 증가)으로 편성되면서 근로연계형 소득지원의 지급 구간·금액 확대 논의가 이어질 전망입니다.',
        updated: TODAY,
        views: 328400,
        link: 'https://www.hometax.go.kr',
        hot: true,
    },
    {
        id: 2,
        slug: 'janyeo-jangryeogeum',
        category: 'worker',
        title: '자녀장려금',
        summary: '저소득 가구의 자녀 1명당 최대 100만원을 지급하는 양육 지원 장려금입니다.',
        description:
            '자녀장려금은 부양자녀가 있는 저소득 가구의 양육 부담을 덜어주기 위한 제도입니다. 근로장려금과 같은 시기에 함께 신청하며, 요건을 충족하면 두 장려금을 모두 받을 수 있습니다.',
        eligibility: '18세 미만 부양자녀가 있고 총소득 7천만원 미만, 재산 2억 4천만원 미만인 가구',
        amount: '부양자녀 1명당 최대 100만원',
        applicationPeriod: '정기신청 5월 1일~31일 (기한 후 신청 11월 30일까지)',
        applicationMethod: '홈택스·손택스 앱, ARS(1544-9944), 세무서 방문',
        agency: '국세청',
        requirements: [
            '2025년 12월 31일 기준 18세 미만 부양자녀 보유',
            '가구 총소득 7,000만원 미만(홑벌이·맞벌이 동일)',
            '가구원 재산 합계 2억 4천만원 미만',
            '부양자녀의 연간 소득금액 100만원 이하',
            '근로·사업·종교인 소득이 있을 것',
        ],
        documents: ['국세청 신청 안내문', '가족관계증명서(필요 시)', '본인 명의 계좌', '신분증'],
        steps: [
            '홈택스 또는 ARS로 근로장려금과 함께 신청',
            '부양자녀 정보 확인',
            '환급 계좌 입력 후 신청',
            '심사 후 8월 말까지 지급',
        ],
        faq: [
            {
                q: '아동수당을 받아도 자녀장려금을 받을 수 있나요?',
                a: '네, 아동수당과 자녀장려금은 별개 제도로 중복 수급이 가능합니다.',
            },
        ],
        tags: ['자녀장려금', '양육지원', '국세청', '현금지원'],
        changes2026: '총소득 기준 7,000만원 미만, 자녀 1인당 최대 100만원 지급 기준이 유지됩니다.',
        outlook2027:
            '2027년 아동기본수당(월 20만~30만원)이 신설되면서 현금성 양육 지원이 재편될 예정이라, 자녀장려금과의 관계 정리가 이뤄질 가능성이 있습니다.',
        updated: TODAY,
        views: 187600,
        link: 'https://www.hometax.go.kr',
        hot: true,
    },
    {
        id: 3,
        slug: 'silup-geubyeo',
        category: 'worker',
        title: '실업급여(구직급여)',
        summary: '비자발적으로 퇴사한 근로자에게 재취업 기간 동안 생계비를 지원합니다.',
        description:
            '실업급여는 고용보험 가입 근로자가 비자발적으로 이직한 뒤 재취업 활동을 하는 기간에 지급되는 급여입니다. 퇴직 전 평균임금의 60%를 지급하되 상·하한액이 적용되며, 고용보험 가입기간과 연령에 따라 120일에서 270일까지 받을 수 있습니다.',
        eligibility: '이직일 이전 18개월간 고용보험 피보험 단위기간 180일 이상인 비자발적 이직자',
        amount: '퇴직 전 평균임금의 60% (1일 상한액 66,000원, 하한액은 최저임금의 80% 수준)',
        applicationPeriod: '이직일 다음 날부터 12개월 이내 (수급기간 내 신청 필수)',
        applicationMethod: '워크넷 구직등록 후 고용24 온라인 신청 또는 관할 고용센터 방문',
        agency: '고용노동부',
        requirements: [
            '이직 전 18개월 중 피보험 단위기간 180일 이상',
            '경영상 해고, 권고사직, 계약만료 등 비자발적 이직',
            '근로 의사와 능력이 있고 적극적으로 재취업 활동을 할 것',
            '워크넷 구직등록 및 수급자격 신청자 온라인 교육 이수',
            '1~4주마다 실업인정일에 재취업활동 신고',
        ],
        documents: ['이직확인서(사업주 제출)', '고용보험 피보험자격 상실신고서', '신분증', '본인 명의 계좌'],
        steps: [
            '사업주가 이직확인서와 피보험자격 상실신고서 제출',
            '워크넷에서 구직 등록',
            '고용24에서 수급자격 신청자 온라인 교육 이수',
            '고용센터를 방문해 수급자격 인정 신청',
            '수급자격 인정 후 1~4주 단위 실업인정 → 급여 지급',
        ],
        faq: [
            {
                q: '자진 퇴사하면 무조건 못 받나요?',
                a: '원칙적으로는 받을 수 없지만 임금체불, 최저임금 미달, 사업장 이전으로 통근이 곤란한 경우, 질병으로 인한 퇴사 등 정당한 이직 사유가 인정되면 수급이 가능합니다.',
            },
            {
                q: '얼마 동안 받을 수 있나요?',
                a: '고용보험 가입기간과 이직 당시 연령에 따라 120일에서 270일까지 지급됩니다.',
            },
        ],
        tags: ['실업급여', '구직급여', '고용보험', '퇴사', '고용24'],
        changes2026:
            '2026년 최저임금이 시간급 10,320원으로 인상되면서 구직급여 하한액도 함께 올랐습니다. 상한액은 1일 66,000원입니다.',
        outlook2027:
            '2027년에는 청년첫취업지원제도(16만명 대상, 월 65만원 구직촉진수당)가 신설되어, 실업급여 수급 요건을 채우지 못한 청년 구직자 지원이 별도로 확대됩니다.',
        updated: TODAY,
        views: 412300,
        link: 'https://www.work24.go.kr',
        hot: true,
    },
    {
        id: 4,
        slug: 'naeil-baeum-card',
        category: 'worker',
        title: '국민내일배움카드',
        summary: '5년간 300만~500만원의 직업훈련비를 지원하는 국민 평생 직업능력 카드입니다.',
        description:
            '국민내일배움카드는 실업자·재직자·자영업자를 가리지 않고 국민 누구나 발급받아 직업훈련비를 지원받을 수 있는 제도입니다. 발급일로부터 5년간 사용할 수 있으며, AI·반도체 등 국가기간전략산업 훈련은 추가 한도와 훈련장려금이 지원됩니다.',
        eligibility: '만 15세 이상 국민 (공무원, 사립학교 교직원, 재학생 등 일부 제외)',
        amount: '1인당 300만~500만원(5년간), 훈련 유형별 자비부담률 15~55%',
        applicationPeriod: '연중 상시',
        applicationMethod: 'HRD-Net 온라인 신청 또는 고용센터 방문',
        agency: '고용노동부',
        requirements: [
            '만 15세 이상 국민 (구직자·재직자·자영업자 모두 가능)',
            '연매출 1억 5천만원 미만 자영업자',
            '월 임금 300만원 미만 재직자는 우선지원 대상',
            '75세 이상, 재학생(졸업예정자 제외), 공무원 등은 발급 제외',
            '국가기간·전략산업직종 훈련은 자비부담 없이 훈련장려금 추가 지급',
        ],
        documents: ['신분증', '(자영업자) 사업자등록증·매출 증빙', '(재직자) 재직증명서'],
        steps: [
            'HRD-Net 회원가입 및 카드 발급 신청',
            '고용센터 심사 후 카드 발급(약 2주 소요)',
            'HRD-Net에서 훈련과정 검색 및 수강신청',
            '카드로 수강료 결제(자비부담금 일부 납부)',
            '출석률 80% 이상 유지 시 훈련장려금 지급',
        ],
        faq: [
            {
                q: '재직 중에도 발급받을 수 있나요?',
                a: '네, 재직자도 발급 가능합니다. 월 임금 300만원 미만이면 우선지원 대상으로 자비부담률이 낮아집니다.',
            },
            {
                q: '한도를 다 쓰면 어떻게 되나요?',
                a: '5년의 유효기간이 끝난 뒤 재발급을 신청할 수 있습니다.',
            },
        ],
        tags: ['국민내일배움카드', '직업훈련', 'HRD-Net', '재취업', 'AI훈련'],
        changes2026: 'AI·디지털 분야 특화 훈련과정이 크게 늘어 K-디지털 트레이닝 참여 인원이 확대되었습니다.',
        outlook2027:
            '2027년 예산안 기준 직업훈련 대상이 56만명에서 72만명으로, AI 특화 훈련은 6만명에서 12만명으로 두 배 확대됩니다.',
        updated: TODAY,
        views: 264800,
        link: 'https://www.hrd.go.kr',
        hot: true,
    },
    {
        id: 5,
        slug: 'cheongnyeon-iljari-doyak',
        category: 'worker',
        title: '청년일자리도약장려금',
        summary: '청년을 채용한 기업과 취업한 청년 모두에게 최대 1,200만원을 지원합니다.',
        description:
            '취업에 어려움을 겪는 청년을 정규직으로 채용한 중소기업 사업주와 해당 청년에게 함께 지원금을 주는 제도입니다. 기업에는 인건비를, 청년에게는 근속 인센티브를 지급해 장기근속을 유도합니다.',
        eligibility: '5인 이상 중소기업이 채용한 만 15~34세 취업애로청년 및 해당 기업',
        amount: '기업 최대 720만원 + 청년 최대 480만원 (유형별 상이)',
        applicationPeriod: '연중 상시 (예산 소진 시 조기 마감)',
        applicationMethod: '고용24 누리집에서 사업 참여 신청 후 운영기관 심사',
        agency: '고용노동부',
        requirements: [
            '만 15~34세 청년 (군 복무기간만큼 최대 39세까지 인정)',
            '취업애로청년 요건(4개월 이상 실업 상태 등) 충족',
            '주 30시간 이상 정규직 채용',
            '5인 이상 고용보험 가입 중소기업(일부 업종은 5인 미만도 가능)',
            '6개월 이상 고용 유지 필수',
        ],
        documents: ['사업자등록증', '4대보험 가입자명부', '근로계약서', '청년 자격 증빙서류'],
        steps: [
            '고용24에서 사업 참여 신청',
            '운영기관의 기업 요건 심사 및 승인',
            '청년 채용 및 근로계약 체결',
            '6개월 근속 후 1차 지원금 신청',
            '이후 근속 기간에 따라 순차 지급',
        ],
        faq: [
            {
                q: '청년도 따로 신청해야 하나요?',
                a: '기업이 사업에 참여하면, 채용된 청년은 근속 요건을 채운 뒤 별도 신청으로 인센티브를 받을 수 있습니다.',
            },
        ],
        tags: ['청년일자리도약장려금', '청년채용', '고용장려금', '중소기업', '고용24'],
        changes2026: '기업 지원(최대 720만원)과 청년 인센티브(최대 480만원) 병행 구조가 유지됩니다.',
        outlook2027:
            '2027년에는 청년 일자리·창업 분야에 5조 1천억원이 투입되고 청년첫취업지원제도가 신설되어 청년 고용 지원 체계가 크게 확대됩니다.',
        updated: TODAY,
        views: 143200,
        link: 'https://www.work24.go.kr',
    },
    {
        id: 6,
        slug: 'durunuri',
        category: 'worker',
        title: '두루누리 사회보험료 지원',
        summary: '10인 미만 사업장 저임금 근로자의 고용보험·국민연금 보험료를 최대 80% 지원합니다.',
        description:
            '근로자 10인 미만 소규모 사업장에서 일하는 저임금 근로자와 그 사업주에게 고용보험과 국민연금 보험료의 일부를 국가가 대신 부담해 주는 제도입니다. 사회보험 사각지대를 줄이기 위한 대표적인 지원 사업입니다.',
        eligibility: '근로자 10명 미만 사업장에 근무하는 월평균 보수 고시기준 미만 근로자와 그 사업주',
        amount: '고용보험·국민연금 보험료의 80% 지원(신규 가입자 기준)',
        applicationPeriod: '연중 상시',
        applicationMethod: '4대사회보험 정보연계센터 또는 국민연금공단·근로복지공단 지사',
        agency: '고용노동부 · 보건복지부',
        requirements: [
            '고용된 근로자 수가 10명 미만인 사업장',
            '월평균 보수가 고시 기준 미만인 근로자',
            '신청일 직전 6개월간 사회보험에 가입하지 않은 신규 가입자',
            '재산 및 종합소득 기준 충족',
            '사업주 부담분과 근로자 부담분을 함께 지원',
        ],
        steps: [
            '4대사회보험 정보연계센터에서 지원 신청',
            '사업장·근로자 요건 심사',
            '승인 후 보험료 고지 시 지원분을 차감해 부과',
        ],
        tags: ['두루누리', '사회보험료', '소상공인', '4대보험', '고용보험'],
        changes2026: '월평균 보수 기준은 매년 고시로 조정되므로 신청 전 4대사회보험 정보연계센터에서 확인해야 합니다.',
        outlook2027: '2027년 사회안전망 강화 기조에 따라 사회보험 사각지대 해소 지원이 계속 확대될 전망입니다.',
        updated: TODAY,
        views: 68400,
        link: 'https://www.insurancesupport.or.kr',
    },
    {
        id: 7,
        slug: 'yuyeon-geunmu-jangryeogeum',
        category: 'worker',
        title: '워라밸일자리·유연근무 장려금',
        summary: '유연근무제를 도입한 중소·중견기업에 근로자 1인당 월 최대 60만원을 지원합니다.',
        description:
            '재택·원격근무, 시차출퇴근제 등 유연근무를 활용하거나 실근로시간을 단축한 중소·중견기업 사업주에게 인건비 일부를 지원하는 제도입니다. 일·생활 균형 문화 확산과 근로시간 단축을 목적으로 합니다.',
        eligibility: '유연근무제 또는 실근로시간 단축을 시행하는 우선지원대상기업·중견기업 사업주',
        amount: '유연근무 근로자 1인당 월 최대 60만원 · 실근로시간 단축 월 최대 30만원',
        applicationPeriod: '연중 상시 (예산 범위 내)',
        applicationMethod: '고용24 누리집 온라인 신청',
        agency: '고용노동부',
        requirements: [
            '우선지원대상기업 또는 중견기업일 것',
            '주 1회 이상 유연근무 활용(재택·원격·선택근무 등)',
            '전자적 방법으로 출퇴근 시간을 관리할 것',
            '유연근무 활용 계획을 사전에 수립·제출',
            '실근로시간 단축형은 주 평균 2시간 이상 단축',
        ],
        tags: ['유연근무', '워라밸', '재택근무', '고용장려금', '중소기업'],
        changes2026: '재택·원격근무 중심의 유연근무 장려금이 유지되며 근태관리 시스템 구축비도 별도 지원됩니다.',
        outlook2027: '2027년에는 일·생활 균형 지원이 저출생 대응 정책과 연계되어 확대될 것으로 예상됩니다.',
        updated: TODAY,
        views: 52100,
        link: 'https://www.worklife.kr',
    },

    /* ─────────────────────────── 주거 ─────────────────────────── */
    {
        id: 11,
        slug: 'jugeo-geubyeo',
        category: 'housing',
        title: '주거급여',
        summary: '기준 중위소득 48% 이하 가구에 월세를 지원하거나 집수리 비용을 지원합니다.',
        description:
            '주거급여는 소득·재산이 일정 기준 이하인 가구에 임차료(월세)를 보조하거나, 자가 주택의 노후도에 따라 수선유지비를 지원하는 기초생활보장 급여입니다. 부양의무자 기준이 적용되지 않아 청년·1인 가구도 신청할 수 있고, 부모와 따로 사는 미혼 청년은 청년 주거급여 분리지급으로 별도 수급이 가능합니다.',
        eligibility: '소득인정액이 2026년 기준 중위소득 48% 이하인 가구 (4인 가구 월 3,117,474원 이하)',
        amount: '지역·가구원수별 기준임대료 한도 내 실제 임차료 지원 (서울 1인 가구 기준 월 약 34만원)',
        applicationPeriod: '연중 상시',
        applicationMethod: '주민센터 방문 신청 또는 복지로 온라인 신청',
        agency: '국토교통부 · 보건복지부',
        requirements: [
            '2026년 기준 중위소득 48% 이하 (1인 1,230,834원 / 4인 3,117,474원)',
            '부양의무자 기준 미적용',
            '임차가구: 지역별 기준임대료 상한 내에서 실제 임차료 지원',
            '자가가구: 주택 노후도에 따라 경보수 457만원~대보수 1,241만원 수선 지원',
            '청년 주거급여 분리지급: 만 19~30세 미만 미혼 청년이 부모와 따로 거주 시 별도 지급',
        ],
        documents: [
            '사회보장급여 신청서',
            '임대차계약서 및 임차료 납부 증빙',
            '소득·재산 신고서',
            '금융정보 등 제공 동의서',
            '신분증',
        ],
        steps: [
            '주민센터 방문 또는 복지로에서 온라인 신청',
            '소득·재산 조사 (시·군·구)',
            'LH 주택조사 (임대차 관계 및 주택 상태 확인)',
            '보장 결정 통지',
            '매월 20일 지정 계좌로 지급',
        ],
        faq: [
            {
                q: '부모님이 소득이 있어도 받을 수 있나요?',
                a: '주거급여는 부양의무자 기준이 폐지되어 부모나 자녀의 소득과 관계없이 본인 가구의 소득인정액만으로 판단합니다.',
            },
            {
                q: '대학생도 신청할 수 있나요?',
                a: '부모와 따로 사는 만 19~30세 미만 미혼 청년은 청년 주거급여 분리지급 제도로 별도 신청이 가능합니다.',
            },
        ],
        tags: ['주거급여', '월세지원', '기초생활보장', '청년주거급여', '기준중위소득'],
        changes2026:
            '2026년 기준 중위소득이 6.51% 인상(4인 649만 4,738원)되면서 선정기준이 완화되었고, 기준임대료도 급지·가구원수별로 1.7만~3.9만원 인상되었습니다.',
        outlook2027:
            '2027년 예산안에서 기준 중위소득을 역대 최대인 6.7% 인상하기로 해 주거급여 수급 대상과 지원액이 다시 한 번 확대될 예정입니다.',
        updated: TODAY,
        views: 296700,
        link: 'https://www.bokjiro.go.kr',
        hot: true,
    },
    {
        id: 12,
        slug: 'cheongnyeon-wolse-jiwon',
        category: 'housing',
        title: '청년월세 특별지원',
        summary: '만 19~34세 청년에게 월 최대 20만원씩 최대 24개월(480만원)을 지원합니다.',
        description:
            '부모와 따로 사는 저소득 무주택 청년의 월세 부담을 덜기 위해 월세를 현금으로 지원하는 제도입니다. 2026년부터 상시 신청 체계로 전환되어 특정 모집 기간을 기다리지 않고 신청할 수 있게 되었습니다.',
        eligibility: '만 19~34세 무주택 청년으로 보증금 5천만원 이하·월세 60만원 이하 주택 거주자',
        amount: '월 최대 20만원 · 최대 24개월(총 480만원)',
        applicationPeriod: '2026년부터 상시 신청',
        applicationMethod: '복지로 온라인 신청 또는 거주지 주민센터 방문',
        agency: '국토교통부',
        requirements: [
            '만 19~34세 무주택 청년(부모와 별도 거주)',
            '보증금 5천만원 이하, 월세 60만원 이하 주택 임차',
            '청년 본인 소득: 기준 중위소득 60% 이하, 재산 1억 2,200만원 이하',
            '원가구(부모 포함) 소득: 기준 중위소득 100% 이하, 재산 4억 7천만원 이하',
            '주택 소유자, 직계존속 주택 임차, 공공임대 거주자 등은 제외',
        ],
        documents: [
            '월세지원 신청서',
            '임대차계약서 사본',
            '월세 이체 증빙서류(최근 3개월)',
            '가족관계증명서',
            '통장 사본',
        ],
        steps: [
            '복지로에서 모의계산으로 자격 확인',
            '복지로 온라인 또는 주민센터 방문 신청',
            '소득·재산 조사 및 자격 검증(약 45일)',
            '지원 결정 통지',
            '매월 25일 계좌로 지급',
        ],
        faq: [
            {
                q: '보증금이 5천만원을 넘으면 안 되나요?',
                a: '보증금 5천만원 이하가 원칙이며, 월세가 60만원을 넘어도 보증금 월세 환산액과 월세액의 합이 70만원 이하면 지원받을 수 있습니다.',
            },
            {
                q: '군 복무를 했으면 나이 기준이 늘어나나요?',
                a: '만 19~34세가 기준이며, 신청 연도 기준으로 판단합니다. 자세한 사항은 거주지 지자체에 확인하세요.',
            },
        ],
        tags: ['청년월세지원', '월세', '청년', '무주택', '복지로'],
        changes2026:
            '2026년부터 한시 사업이 아닌 상시 신청 제도로 전환되었고, 지원 기간이 최대 24개월(총 480만원)로 확대되었습니다.',
        outlook2027:
            '2027년 예산안에서 청년 월세 지원의 소득 기준을 기준 중위소득 60% 이하에서 100% 이하로 대폭 완화해 대상이 크게 늘어날 예정입니다.',
        updated: TODAY,
        views: 341500,
        link: 'https://www.bokjiro.go.kr',
        hot: true,
    },
    {
        id: 13,
        slug: 'beotimmok-jeonse-daechul',
        category: 'housing',
        title: '청년전용 버팀목 전세자금대출',
        summary: '만 19~34세 청년에게 최대 2억원까지 저금리 전세자금을 빌려줍니다.',
        description:
            '주택도시기금이 재원인 청년 전용 전세자금 대출로, 시중은행보다 낮은 금리로 전세보증금을 빌릴 수 있습니다. 무주택 청년의 주거 사다리 역할을 하는 대표적인 정책 대출입니다.',
        eligibility: '만 19~34세 무주택 세대주로 부부합산 연소득 5천만원 이하인 자',
        amount: '최대 2억원 (임차보증금의 80% 이내)',
        applicationPeriod: '연중 상시 (임대차계약일 또는 잔금지급일로부터 3개월 이내)',
        applicationMethod: '기금e든든 온라인 신청 또는 우리·국민·농협·신한·하나은행 방문',
        agency: '국토교통부 · 주택도시보증공사',
        requirements: [
            '만 19세 이상 34세 이하 무주택 세대주(예비 세대주 포함)',
            '부부합산 연소득 5,000만원 이하',
            '순자산가액이 기금 기준 이하',
            '임차 전용면적 85㎡ 이하(수도권 외 읍·면 100㎡ 이하)',
            '임차보증금 3억원 이하',
        ],
        documents: [
            '확정일자부 임대차계약서',
            '계약금 5% 이상 납입 영수증',
            '주민등록등본·가족관계증명서',
            '소득증빙서류(원천징수영수증 등)',
            '재직증명서 또는 사업자등록증',
        ],
        steps: [
            '기금e든든에서 자격 조회 및 대출 신청',
            '은행 방문하여 서류 제출',
            '심사 및 승인',
            '전세 잔금일에 임대인 계좌로 대출금 지급',
        ],
        faq: [
            {
                q: '중소기업 취업 청년 전세대출과 뭐가 다른가요?',
                a: '중소기업취업청년 전월세보증금대출은 대상이 중소·중견기업 재직자로 한정되지만 금리가 더 낮고 한도는 1억원입니다. 조건을 비교해 유리한 상품을 선택하면 됩니다.',
            },
        ],
        tags: ['버팀목대출', '전세자금대출', '청년전세', '주택도시기금', '무주택'],
        changes2026: '금리는 소득과 보증금 구간에 따라 차등 적용되며, 기금e든든을 통한 비대면 신청이 일반화되었습니다.',
        outlook2027:
            '2027년에는 청년 주거 예산이 18조 5천억원으로 확대되며 전용면적 50~84㎡ 규모의 보편형 공공임대 2만호가 새로 공급됩니다.',
        updated: TODAY,
        views: 218900,
        link: 'https://nhuf.molit.go.kr',
    },
    {
        id: 14,
        slug: 'sinsaenga-teungnye-daechul',
        category: 'housing',
        title: '신생아 특례 주택 구입·전세자금 대출',
        summary: '2년 이내 출산 가구에 최저 수준 금리로 주택 구입 5억원, 전세 3억원을 지원합니다.',
        description:
            '대출 신청일 기준 2년 이내에 출산한 무주택 가구에 파격적인 저금리로 주택 구입자금 또는 전세자금을 빌려주는 제도입니다. 저출생 대응을 위한 핵심 주거 정책으로, 추가 출산 시 금리가 더 낮아집니다.',
        eligibility: '대출 신청일 기준 2년 이내 출산한 무주택 세대주 (2023년 1월 1일 이후 출생아 가구)',
        amount: '주택 구입 최대 5억원 · 전세자금 최대 3억원',
        applicationPeriod: '연중 상시 (출산일로부터 2년 이내)',
        applicationMethod: '기금e든든 온라인 신청 또는 취급 은행 방문',
        agency: '국토교통부',
        requirements: [
            '대출 신청일 기준 2년 내 출산(2023년 1월 1일 이후 출생아)',
            '무주택 세대주 또는 1주택자(대환 목적)',
            '부부합산 연소득 기준 충족 (완화된 소득 요건 적용)',
            '구입: 주택가격 9억원 이하, 전용면적 85㎡ 이하',
            '전세: 보증금 수도권 5억원 이하, 지방 4억원 이하',
            '추가 출산 시 자녀 1명당 금리 추가 인하 및 특례기간 연장',
        ],
        documents: ['가족관계증명서', '출생증명서 또는 기본증명서', '주민등록등본', '소득증빙서류', '매매·임대차계약서'],
        steps: [
            '기금e든든에서 자격 조회',
            '온라인 신청 또는 은행 방문 접수',
            '서류 심사 및 담보 평가',
            '대출 승인 및 실행',
        ],
        faq: [
            {
                q: '입양한 아동도 인정되나요?',
                a: '네, 2023년 1월 1일 이후 출생한 자녀를 입양한 경우도 대상에 포함됩니다.',
            },
            {
                q: '이미 대출이 있는데 갈아탈 수 있나요?',
                a: '기존 주택담보대출을 신생아 특례 대출로 대환하는 것이 가능합니다. 조건은 은행에 문의하세요.',
            },
        ],
        tags: ['신생아특례대출', '출산가구', '디딤돌대출', '전세대출', '저출생'],
        changes2026: '소득 요건이 지속적으로 완화되어 맞벌이 신혼·출산 가구의 이용 문턱이 낮아졌습니다.',
        outlook2027:
            '2027년 혼인·출산·양육 3종 패키지 신설과 맞물려 출산 가구 주거 지원이 더욱 강화될 전망입니다.',
        updated: TODAY,
        views: 176300,
        link: 'https://nhuf.molit.go.kr',
        hot: true,
    },
    {
        id: 15,
        slug: 'didimdol-daechul',
        category: 'housing',
        title: '디딤돌 주택구입자금 대출',
        summary: '무주택 서민의 내 집 마련을 위한 최대 2억 5천만원 저금리 구입자금 대출입니다.',
        description:
            '주택도시기금에서 지원하는 대표적인 주택 구입자금 대출로, 무주택 서민이 시중금리보다 낮은 고정금리로 주택을 구입할 수 있게 돕습니다. 신혼부부와 다자녀 가구는 한도와 금리에서 우대를 받습니다.',
        eligibility: '부부합산 연소득 6천만원 이하(신혼·2자녀 이상 8,500만원 이하) 무주택 세대주',
        amount: '일반 최대 2억 5천만원 · 신혼가구 4억원 · 2자녀 이상 4억원',
        applicationPeriod: '소유권 이전등기일로부터 3개월 이내',
        applicationMethod: '기금e든든 온라인 신청 또는 취급 은행 방문',
        agency: '국토교통부',
        requirements: [
            '무주택 세대주 (세대원 전원 무주택)',
            '부부합산 연소득 6,000만원 이하(생애최초·신혼·다자녀는 완화)',
            '순자산가액 기금 기준 이하',
            '주택가격 5억원 이하(신혼·2자녀 이상 6억원 이하)',
            '전용면적 85㎡ 이하(수도권 외 읍·면 100㎡ 이하)',
        ],
        documents: ['매매계약서', '주민등록등본', '가족관계증명서', '소득·재직 증빙서류', '등기부등본'],
        steps: ['기금e든든에서 자격 조회', '온라인 또는 은행 신청', '심사 및 승인', '소유권 이전과 동시에 대출 실행'],
        tags: ['디딤돌대출', '주택구입', '내집마련', '주택도시기금', '생애최초'],
        changes2026: '생애최초 주택 구입자에 대한 우대 금리와 한도가 유지되고 있습니다.',
        outlook2027: '2027년 주거 예산 확대와 함께 정책 대출의 소득 요건 완화가 논의되고 있습니다.',
        updated: TODAY,
        views: 154200,
        link: 'https://nhuf.molit.go.kr',
    },
    {
        id: 16,
        slug: 'haengbok-jutaek',
        category: 'housing',
        title: '행복주택·공공임대주택',
        summary: '청년·신혼부부·고령자에게 시세의 60~80% 수준으로 임대주택을 공급합니다.',
        description:
            '대학생, 청년, 신혼부부, 고령자, 주거급여 수급자 등을 대상으로 직장과 학교가 가까운 곳에 시세보다 저렴하게 공급하는 공공임대주택입니다. 최대 6년(자녀가 있으면 10년)까지 거주할 수 있습니다.',
        eligibility: '청년, 신혼부부, 한부모가족, 고령자, 주거급여 수급자 등 계층별 소득·자산 기준 충족자',
        amount: '주변 시세의 60~80% 수준 임대료',
        applicationPeriod: '입주자 모집공고별 상이 (연중 수시 공고)',
        applicationMethod: 'LH 청약플러스 또는 각 지방공사(SH·GH 등) 홈페이지',
        agency: '국토교통부 · LH · 지방공사',
        requirements: [
            '청년: 만 19~39세 미혼 무주택자, 소득 기준 충족',
            '신혼부부: 혼인 7년 이내 또는 예비 신혼부부',
            '대학생: 인근 대학 재학 중인 미혼 무주택자',
            '고령자: 만 65세 이상 무주택 세대구성원',
            '해당 지역 거주자 또는 직장인 우선 공급',
        ],
        documents: ['주민등록등본', '가족관계증명서', '소득·자산 증빙서류', '재학·재직증명서(해당 시)'],
        steps: [
            'LH 청약플러스에서 모집공고 확인',
            '온라인 청약 신청',
            '서류 심사 및 소득·자산 조사',
            '당첨자 발표 및 계약 체결',
            '입주',
        ],
        faq: [
            {
                q: '얼마나 오래 살 수 있나요?',
                a: '기본 6년이며, 자녀가 있는 신혼부부는 최대 10년까지 거주할 수 있습니다. 고령자·주거급여 수급자는 최대 20년입니다.',
            },
        ],
        tags: ['행복주택', '공공임대', 'LH', '청년주택', '신혼부부'],
        changes2026: '청약 시스템이 LH 청약플러스로 통합되어 신청 절차가 간소화되었습니다.',
        outlook2027:
            '2027년에는 전용 50~84㎡ 규모의 보편형 공공임대 2만호와 청년지원주택 1만호가 신규 공급됩니다.',
        updated: TODAY,
        views: 189400,
        link: 'https://apply.lh.or.kr',
    },

    /* ─────────────────────── 출산·육아 ─────────────────────── */
    {
        id: 21,
        slug: 'cheotmannam-iyongkwon',
        category: 'parenting',
        title: '첫만남이용권',
        summary: '출생아 1명당 첫째 200만원, 둘째 이상 300만원의 바우처를 지급합니다.',
        description:
            '출산 초기에 발생하는 양육 비용 부담을 덜기 위해 출생아 1명당 지급하는 일시금 바우처입니다. 국민행복카드에 포인트로 충전되며, 유흥·사행업종을 제외한 대부분의 업종에서 사용할 수 있습니다.',
        eligibility: '2022년 1월 1일 이후 출생하여 주민등록번호를 부여받은 모든 아동',
        amount: '첫째아 200만원 · 둘째아 이상 300만원',
        applicationPeriod: '출생일로부터 1년 이내 신청, 바우처는 지급일로부터 1년 이내 사용',
        applicationMethod: '복지로 온라인 신청 또는 주민센터 방문(출생신고와 동시 신청 가능)',
        agency: '보건복지부',
        requirements: [
            '2022년 1월 1일 이후 출생 아동',
            '주민등록번호를 부여받고 출생신고 완료',
            '국민행복카드 발급 필요',
            '출생일로부터 1년 이내 신청',
            '보호자 명의 국민행복카드에 포인트 충전',
        ],
        documents: ['첫만남이용권 지급 신청서', '국민행복카드', '신분증', '출생증명서(온라인 출생신고 시 불필요)'],
        steps: [
            '출생신고와 동시에 주민센터에서 신청(행복출산 원스톱 서비스)',
            '또는 복지로에서 온라인 신청',
            '국민행복카드 발급 또는 기존 카드 연계',
            '심사 후 카드에 바우처 포인트 충전',
            '유흥·사행업종 등을 제외한 가맹점에서 사용',
        ],
        faq: [
            {
                q: '부모급여와 중복으로 받을 수 있나요?',
                a: '네, 첫만남이용권·부모급여·아동수당은 각각 별개 제도로 모두 함께 받을 수 있습니다.',
            },
            {
                q: '기한 내에 다 못 쓰면 어떻게 되나요?',
                a: '지급일로부터 1년 이내에 사용해야 하며, 기한이 지나면 잔액이 소멸되므로 주의해야 합니다.',
            },
        ],
        tags: ['첫만남이용권', '출산지원금', '국민행복카드', '바우처', '출산'],
        changes2026: '첫째아 200만원, 둘째아 이상 300만원의 차등 지급 구조가 유지됩니다.',
        outlook2027:
            '2027년부터 혼인·출산·양육 3종 패키지가 신설되면서 첫만남이용권·부모급여 등 기존 현금성 지원이 출산지원금(첫째 1,000만원, 셋째 이상 1,500만원, 지방 거주 최대 2,000만원)으로 통합·확대될 예정입니다.',
        updated: TODAY,
        views: 302100,
        link: 'https://www.bokjiro.go.kr',
        hot: true,
    },
    {
        id: 22,
        slug: 'bumo-geubyeo',
        category: 'parenting',
        title: '부모급여',
        summary: '만 0세 아동에게 월 100만원, 만 1세 아동에게 월 50만원을 지급합니다.',
        description:
            '출산 후 소득이 줄어드는 시기에 가정의 경제적 부담을 덜고 부모가 아이와 함께하는 시간을 보장하기 위해 매월 지급하는 현금성 급여입니다. 어린이집을 이용하는 경우 보육료 바우처로 전환되어 지원됩니다.',
        eligibility: '만 0~1세(0~23개월) 아동을 양육하는 가정',
        amount: '만 0세 월 100만원 · 만 1세 월 50만원',
        applicationPeriod: '출생일 포함 60일 이내 신청 시 출생월부터 소급 지급',
        applicationMethod: '복지로 온라인 신청 또는 주민센터 방문',
        agency: '보건복지부',
        requirements: [
            '만 0~23개월 아동',
            '대한민국 국적 및 주민등록번호 보유',
            '출생일 포함 60일 이내 신청(이후 신청 시 신청월부터 지급)',
            '어린이집 이용 시 보육료 바우처로 전환 지급',
            '종일제 아이돌봄서비스 이용 시 중복 불가(선택)',
        ],
        documents: ['사회보장급여 신청서', '신분증', '통장 사본', '(해당 시) 어린이집 재원 확인'],
        steps: [
            '출생신고 시 행복출산 원스톱 서비스로 함께 신청',
            '또는 복지로·주민센터에서 신청',
            '자격 확인 후 매월 25일 지급',
        ],
        faq: [
            {
                q: '어린이집에 보내면 부모급여를 못 받나요?',
                a: '어린이집을 이용하면 보육료 바우처로 지원되며, 만 0세는 바우처 금액이 부모급여보다 적을 경우 차액을 현금으로 받습니다.',
            },
            {
                q: '아동수당과 중복 수급이 되나요?',
                a: '네, 부모급여와 아동수당은 별개 제도로 함께 받을 수 있습니다.',
            },
        ],
        tags: ['부모급여', '영아수당', '양육수당', '출산', '육아'],
        changes2026: '만 0세 월 100만원, 만 1세 월 50만원 지급 기준이 유지되고 있습니다.',
        outlook2027:
            '2027년에는 부모급여를 포함한 현금성 양육 지원이 아동기본수당(월 20만~30만원)과 출산지원금 체계로 통합·재편될 예정입니다.',
        updated: TODAY,
        views: 358900,
        link: 'https://www.bokjiro.go.kr',
        hot: true,
    },
    {
        id: 23,
        slug: 'adong-sudang',
        category: 'parenting',
        title: '아동수당',
        summary: '만 8세 미만 모든 아동에게 소득과 관계없이 매월 10만원을 지급합니다.',
        description:
            '아동의 건강한 성장 환경을 조성하고 기본적 권리와 복지를 증진하기 위해 소득·재산 조사 없이 모든 아동에게 지급하는 보편적 수당입니다.',
        eligibility: '만 8세 미만(0~95개월) 대한민국 국적 아동',
        amount: '아동 1명당 월 10만원',
        applicationPeriod: '출생일 포함 60일 이내 신청 시 출생월부터 소급 지급',
        applicationMethod: '복지로 온라인 신청 또는 주민센터 방문',
        agency: '보건복지부',
        requirements: [
            '만 8세 미만(0~95개월) 아동',
            '대한민국 국적 및 주민등록번호 보유',
            '소득·재산 기준 없음(보편 지급)',
            '난민 인정 아동도 지급 대상',
            '해외 체류 90일 이상 시 지급 정지',
        ],
        documents: ['사회보장급여 신청서', '신분증', '통장 사본'],
        steps: ['출생신고 시 함께 신청', '또는 복지로·주민센터 신청', '매월 25일 지급'],
        faq: [
            {
                q: '소득이 높아도 받을 수 있나요?',
                a: '네, 아동수당은 소득·재산 조사 없이 만 8세 미만 모든 아동에게 지급되는 보편 수당입니다.',
            },
        ],
        tags: ['아동수당', '보편수당', '양육', '복지로', '월10만원'],
        changes2026: '만 8세 미만 아동에게 월 10만원을 지급하는 기준이 유지되며, 연령 확대 논의가 진행 중입니다.',
        outlook2027:
            '2027년에는 아동기본수당이 신설되어 월 20만~30만원 수준으로 지급될 예정이며, 기존 아동수당 체계와 통합됩니다.',
        updated: TODAY,
        views: 274600,
        link: 'https://www.bokjiro.go.kr',
        hot: true,
    },
    {
        id: 24,
        slug: 'yuga-hyujik-geubyeo',
        category: 'parenting',
        title: '육아휴직급여',
        summary: '육아휴직 기간에 최대 월 250만원의 급여를 지급하며 사후지급금은 폐지되었습니다.',
        description:
            '만 8세 이하 또는 초등학교 2학년 이하 자녀를 양육하기 위해 육아휴직을 사용한 근로자에게 고용보험에서 지급하는 급여입니다. 2025년부터 급여 상한이 크게 올랐고, 휴직 기간 중 급여를 전액 받을 수 있도록 사후지급 방식이 폐지되었습니다.',
        eligibility: '육아휴직을 30일 이상 사용하고 고용보험 피보험 단위기간이 180일 이상인 근로자',
        amount: '1~3개월 월 최대 250만원 · 4~6개월 월 최대 200만원 · 7개월 이후 월 최대 160만원',
        applicationPeriod: '육아휴직 시작 후 1개월부터 종료 후 12개월 이내',
        applicationMethod: '고용24 온라인 신청 또는 관할 고용센터 방문',
        agency: '고용노동부',
        requirements: [
            '만 8세 이하 또는 초등학교 2학년 이하 자녀 양육',
            '육아휴직을 30일 이상 부여받았을 것',
            '육아휴직 개시일 이전 고용보험 피보험 단위기간 180일 이상',
            '부모가 함께 사용하면 첫 6개월간 상향된 급여 적용',
            '한부모 근로자는 별도의 우대 지급률 적용',
        ],
        documents: [
            '육아휴직 급여 신청서',
            '육아휴직 확인서(사업주 발급)',
            '통상임금을 확인할 수 있는 자료',
            '가족관계증명서',
        ],
        steps: [
            '사업주에게 육아휴직 신청 및 확인서 발급',
            '육아휴직 시작 1개월 후부터 고용24에서 급여 신청',
            '매월 또는 일괄 신청 가능',
            '심사 후 지정 계좌로 지급',
        ],
        faq: [
            {
                q: '사후지급금이 폐지됐다는 게 무슨 뜻인가요?',
                a: '예전에는 급여의 25%를 복직 6개월 후에 지급했지만, 2025년부터는 육아휴직 기간 중에 전액을 받을 수 있게 되었습니다.',
            },
            {
                q: '부부가 같이 쓰면 더 받나요?',
                a: '생후 18개월 이내 자녀에 대해 부모가 함께 육아휴직을 사용하면 첫 6개월간 급여가 상향 적용됩니다.',
            },
        ],
        tags: ['육아휴직급여', '육아휴직', '고용보험', '사후지급금폐지', '고용24'],
        changes2026:
            '육아휴직 급여 상한이 기간별로 250만원·200만원·160만원으로 적용되며, 사후지급금 폐지가 이어지고 있습니다.',
        outlook2027: '2027년 저출생 대응 예산 확대에 따라 육아휴직 사용률 제고를 위한 추가 지원이 예상됩니다.',
        updated: TODAY,
        views: 387200,
        link: 'https://www.work24.go.kr',
        hot: true,
    },
    {
        id: 25,
        slug: 'yugagi-geunro-danchuk',
        category: 'parenting',
        title: '육아기 근로시간 단축급여',
        summary: '2026년부터 상한액이 월 250만원으로 인상된 근로시간 단축 지원 급여입니다.',
        description:
            '육아휴직 대신 근로시간을 줄여 일과 육아를 병행하는 근로자에게 줄어든 임금의 일부를 보전해 주는 제도입니다. 2026년 1월 1일부터 상한액이 인상되어 실수령액이 늘었습니다.',
        eligibility: '만 12세 이하 또는 초등학교 6학년 이하 자녀를 둔 근로자로 근로시간을 단축한 자',
        amount: '최초 10시간 단축분 통상임금 100%(월 상한 250만원) · 나머지 단축분 80%(월 상한 160만원)',
        applicationPeriod: '단축 시작 후 1개월부터 종료 후 12개월 이내',
        applicationMethod: '고용24 온라인 신청 또는 관할 고용센터 방문',
        agency: '고용노동부',
        requirements: [
            '만 12세 이하 또는 초등학교 6학년 이하 자녀 양육',
            '주 소정근로시간을 15시간 이상 35시간 이하로 단축',
            '고용보험 피보험 단위기간 180일 이상',
            '육아휴직 미사용분을 근로시간 단축으로 전환 사용 가능',
            '최대 사용 기간은 육아휴직 잔여기간을 합산해 산정',
        ],
        documents: ['급여 신청서', '근로시간 단축 확인서', '통상임금 확인 자료', '가족관계증명서'],
        steps: [
            '사업주와 근로시간 단축 협의 및 확인서 발급',
            '단축 시작 1개월 후 고용24에서 신청',
            '심사 후 매월 지급',
        ],
        faq: [
            {
                q: '2026년에 무엇이 달라졌나요?',
                a: '최초 10시간 단축분의 월 상한액이 220만원에서 250만원으로, 나머지 단축분은 150만원에서 160만원으로 인상되었습니다.',
            },
        ],
        tags: ['육아기근로시간단축', '단축급여', '워라밸', '고용보험', '2026인상'],
        changes2026:
            '2026년 1월 1일부터 최초 10시간 단축분 상한이 월 220만원에서 250만원으로, 나머지 단축분은 150만원에서 160만원으로 인상되었습니다.',
        outlook2027: '2027년에도 일·가정 양립 지원 강화 기조가 이어져 대상 자녀 연령 확대가 논의되고 있습니다.',
        updated: TODAY,
        views: 142800,
        link: 'https://www.work24.go.kr',
        isNew: true,
    },
    {
        id: 26,
        slug: 'imsin-chulsan-jinryobi',
        category: 'parenting',
        title: '임신·출산 진료비 지원(국민행복카드)',
        summary: '임신 1회당 단태아 100만원, 다태아 140만원의 진료비 바우처를 지원합니다.',
        description:
            '임신·출산과 관련된 진료비 부담을 덜기 위해 국민행복카드에 바우처를 지급하는 제도입니다. 산부인과 진료뿐 아니라 모든 진료과목의 진료비와 약제비에 사용할 수 있습니다.',
        eligibility: '임신이 확인된 건강보험 가입자 또는 피부양자',
        amount: '단태아 100만원 · 다태아 140만원 (분만취약지 20만원 추가)',
        applicationPeriod: '임신 확인 후부터 분만예정일 이후 2년까지 사용',
        applicationMethod: '국민건강보험공단 지사, 카드사 또는 정부24·The건강보험 앱',
        agency: '보건복지부 · 국민건강보험공단',
        requirements: [
            '의료기관에서 발급한 임신확인서 제출',
            '건강보험 가입자 또는 피부양자',
            '국민행복카드 발급 필요',
            '분만예정일로부터 2년 이내 사용',
            '1세 미만 영유아의 진료비에도 사용 가능',
        ],
        documents: ['임신확인서', '신분증', '국민행복카드'],
        steps: [
            '병원에서 임신확인서 발급',
            '카드사 또는 건강보험공단에서 국민행복카드 신청',
            '바우처 충전 확인',
            '요양기관에서 결제 시 자동 차감',
        ],
        tags: ['임신바우처', '국민행복카드', '진료비지원', '출산', '건강보험'],
        changes2026: '단태아 100만원, 다태아 140만원 지원 기준이 유지되며 사용 기간은 분만예정일 이후 2년입니다.',
        outlook2027: '2027년 저출생 대응 패키지 확대에 따라 임신·출산 의료비 지원 강화가 예상됩니다.',
        updated: TODAY,
        views: 167300,
        link: 'https://www.nhis.or.kr',
    },
    {
        id: 27,
        slug: 'ai-dolbom-service',
        category: 'parenting',
        title: '아이돌봄 서비스',
        summary: '만 12세 이하 아동 가정에 돌보미가 방문해 아이를 돌봐주는 서비스입니다.',
        description:
            '맞벌이 등으로 양육 공백이 발생한 가정에 아이돌보미가 직접 방문해 돌봄을 제공하는 서비스입니다. 소득 수준에 따라 정부가 이용요금의 상당 부분을 지원합니다.',
        eligibility: '만 12세 이하 아동을 둔 맞벌이·취업 한부모·다자녀 등 양육 공백 가정',
        amount: '소득 유형별 정부지원 비율 차등(가~라형), 기준 중위소득 250% 이하까지 지원',
        applicationPeriod: '연중 상시',
        applicationMethod: '아이돌봄서비스 홈페이지 또는 주민센터 신청',
        agency: '여성가족부',
        requirements: [
            '만 12세 이하 아동을 양육하는 가정',
            '맞벌이, 취업 한부모, 장애부모, 다자녀 등 양육공백 인정 가구',
            '기준 중위소득 250% 이하 가구에 정부지원 적용',
            '시간제(기본형·종합형)와 영아종일제 중 선택',
            '국민행복카드로 결제',
        ],
        documents: ['서비스 신청서', '가족관계증명서', '재직·재학 증명서 등 양육공백 증빙', '소득 증빙자료'],
        steps: [
            '아이돌봄서비스 홈페이지에서 정부지원 신청',
            '주민센터 소득 판정(약 2주)',
            '지원 유형 결정 통지',
            '서비스 연계 및 아이돌보미 매칭',
            '이용 후 국민행복카드로 결제',
        ],
        tags: ['아이돌봄서비스', '돌봄', '맞벌이', '여성가족부', '양육공백'],
        changes2026: '정부지원 소득 기준이 기준 중위소득 250% 이하까지 확대되어 지원 대상이 늘었습니다.',
        outlook2027: '2027년에는 돌봄 국가책임 강화 기조에 따라 아이돌보미 확충과 지원 확대가 계획되어 있습니다.',
        updated: TODAY,
        views: 128400,
        link: 'https://www.idolbom.go.kr',
    },

    /* ─────────────────────── 교육·자기계발 ─────────────────────── */
    {
        id: 31,
        slug: 'gukga-janghakgeum',
        category: 'education',
        title: '국가장학금',
        summary: '2026년부터 9구간까지 확대되어 약 150만명이 등록금을 지원받습니다.',
        description:
            '소득 수준에 따라 대학 등록금을 차등 지원하는 대한민국 대표 장학 제도입니다. 2026년에는 지원 구간이 9구간까지 확대되고 총 규모가 역대 최대인 5조 1,161억원으로 늘어 중산층 가구까지 혜택이 넓어졌습니다.',
        eligibility: '국내 대학에 재학 중인 학자금 지원구간 9구간 이하 대한민국 국적 학생',
        amount: '기초·차상위 등록금 전액 ~ 9구간 연 100만원 (다자녀 가구 추가 지원)',
        applicationPeriod: '1학기 11~12월(1차)·2월(2차) / 2학기 5~6월(1차)·8월(2차)',
        applicationMethod: '한국장학재단 홈페이지 또는 모바일 앱',
        agency: '교육부 · 한국장학재단',
        requirements: [
            '학자금 지원구간 9구간 이하',
            '직전 학기 12학점 이상 이수 및 성적 100점 만점에 80점(B학점) 이상',
            '기초·차상위 및 1~3구간은 C학점(70점) 경고제 적용',
            '소득·재산 조사를 위한 가구원 동의 필수',
            '신입생·편입생·재입학생은 첫 학기 성적 기준 미적용',
        ],
        documents: ['가족관계증명서', '기본증명서', '가구원 정보제공 동의', '(해당 시) 소득·재산 증빙'],
        steps: [
            '한국장학재단 홈페이지에서 신청',
            '가구원 정보제공 동의 완료(필수)',
            '서류 제출 및 소득분위 산정(약 8주)',
            '학자금 지원구간 확정 및 결과 확인',
            '대학에 등록금 차감 형태로 지급',
        ],
        faq: [
            {
                q: '9구간도 받을 수 있나요?',
                a: '2026년부터 9구간까지 지원이 확대되어 연 100만원(학기당 50만원)을 받을 수 있습니다. 다자녀 가구는 첫째·둘째 135만원, 셋째 이상 200만원까지 지원됩니다.',
            },
            {
                q: '성적이 안 되면 못 받나요?',
                a: '직전 학기 12학점 이상 이수와 B학점(80점) 이상이 원칙이지만, 기초·차상위 및 1~3구간은 C학점 경고제가 적용되어 한 번은 구제받을 수 있습니다.',
            },
            {
                q: '가구원 동의를 꼭 해야 하나요?',
                a: '네, 소득분위 산정을 위해 부모 등 가구원의 정보제공 동의가 필수입니다. 동의가 없으면 지원구간이 산정되지 않아 탈락합니다.',
            },
        ],
        tags: ['국가장학금', '등록금', '한국장학재단', '학자금', '9구간'],
        changes2026:
            '지원 구간이 8구간에서 9구간으로 확대되어 9구간 학생도 연 100만원을 받을 수 있고, 8구간 이하 다자녀 가구의 셋째 이상 자녀는 등록금 전액이 지원됩니다. 총 규모는 5조 1,161억원, 수혜 인원은 약 150만명입니다.',
        outlook2027:
            '2027년 신입생부터 지방 국립대 등록금이 전액 장학금으로 지원될 예정이어서, 지역 대학 진학자의 학비 부담이 크게 줄어듭니다.',
        updated: TODAY,
        views: 412700,
        link: 'https://www.kosaf.go.kr',
        hot: true,
    },
    {
        id: 32,
        slug: 'gyoyuk-geubyeo',
        category: 'education',
        title: '교육급여·교육활동지원비',
        summary: '기준 중위소득 50% 이하 가구의 초·중·고 학생에게 교육활동지원비를 지급합니다.',
        description:
            '저소득층 학생의 교육 기회를 보장하기 위해 교육활동지원비, 교과서대, 입학금·수업료를 지원하는 기초생활보장 급여입니다. 부양의무자 기준이 적용되지 않습니다.',
        eligibility: '소득인정액이 2026년 기준 중위소득 50% 이하인 가구의 초·중·고 재학생',
        amount: '초등학생·중학생·고등학생 학년별 교육활동지원비 연 1회 지급 + 교과서대·입학금·수업료 실비',
        applicationPeriod: '연중 상시 (집중 신청기간 3월)',
        applicationMethod: '주민센터 방문 또는 복지로·교육비원클릭 신청',
        agency: '교육부 · 보건복지부',
        requirements: [
            '2026년 기준 중위소득 50% 이하(4인 가구 월 3,247,369원 이하)',
            '초·중·고등학교 및 특수학교 재학생',
            '부양의무자 기준 미적용',
            '교육활동지원비는 카드 포인트 또는 현금으로 지급',
            '고등학생은 교과서대·입학금·수업료 추가 지원',
        ],
        documents: ['사회보장급여 신청서', '재학증명서', '금융정보 제공 동의서', '신분증'],
        steps: ['주민센터 또는 복지로에서 신청', '소득·재산 조사', '교육청 자격 확인', '학기 초 일괄 지급'],
        tags: ['교육급여', '교육활동지원비', '저소득층', '기초생활보장', '학비지원'],
        changes2026: '2026년 기준 중위소득 6.51% 인상으로 선정기준이 완화되어 대상자가 늘었습니다.',
        outlook2027: '2027년 기준 중위소득이 6.7% 추가 인상되어 교육급여 대상이 더 확대될 예정입니다.',
        updated: TODAY,
        views: 96200,
        link: 'https://www.bokjiro.go.kr',
    },
    {
        id: 33,
        slug: 'pyeongsaeng-gyoyuk-voucher',
        category: 'education',
        title: '평생교육바우처',
        summary: '성인 학습자에게 연간 35만원의 평생교육 수강료를 지원합니다.',
        description:
            '학습을 원하는 성인에게 평생교육 강좌 수강료와 교재비를 지원하는 바우처입니다. 어학, 자격증, 인문교양, 직무능력 등 다양한 분야의 지정 기관 강좌에서 사용할 수 있습니다.',
        eligibility: '만 19세 이상 성인 중 기준 중위소득 65% 이하 가구원',
        amount: '1인당 연간 35만원 (우수 학습자는 추가 지원)',
        applicationPeriod: '연 1~2회 모집 (평생교육바우처 누리집 공고)',
        applicationMethod: '평생교육바우처 홈페이지 온라인 신청',
        agency: '교육부 · 국가평생교육진흥원',
        requirements: [
            '만 19세 이상 성인',
            '가구 소득이 기준 중위소득 65% 이하',
            '평생교육이용권 지정 사용기관의 강좌 수강',
            '수강료·교재비에 사용 가능',
            '발급 연도 내 사용 필수',
        ],
        documents: ['신청서', '신분증', '소득 확인을 위한 건강보험료 납부확인서'],
        steps: ['평생교육바우처 누리집에서 신청', '자격 심사 및 선정', '바우처 카드 발급', '지정 기관 강좌 수강 신청 및 결제'],
        tags: ['평생교육바우처', '평생학습', '자기계발', '성인학습', '교육부'],
        changes2026: '연간 35만원 지원 기준이 유지되며, 온라인 강좌 사용처가 지속 확대되고 있습니다.',
        outlook2027: '2027년 AI·디지털 역량 강화 기조에 따라 관련 강좌 지원이 확대될 전망입니다.',
        updated: TODAY,
        views: 74500,
        link: 'https://www.lllcard.kr',
    },
    {
        id: 34,
        slug: 'cheongnyeon-dojeon-jiwon',
        category: 'education',
        title: '청년도전지원사업',
        summary: '구직을 단념한 청년에게 프로그램 참여수당과 인센티브를 지원합니다.',
        description:
            '장기간 구직활동을 하지 않은 청년이 자신감을 회복하고 노동시장에 다시 들어올 수 있도록 맞춤형 프로그램과 참여수당을 제공하는 사업입니다.',
        eligibility: '만 18~34세 구직단념청년, 자립준비청년, 청소년쉼터 입퇴소 청년 등',
        amount: '단기 프로그램 참여수당 월 50만원 · 중장기 이수 시 인센티브 최대 300만원',
        applicationPeriod: '연중 수시 (운영기관별 모집)',
        applicationMethod: '거주지 청년도전지원사업 운영기관 방문 또는 온라인 신청',
        agency: '고용노동부',
        requirements: [
            '만 18~34세 청년',
            '최근 6개월 이상 취업·교육·직업훈련에 참여하지 않은 구직단념 상태',
            '자립준비청년, 보호연장청년 등은 구직단념 요건 없이 참여 가능',
            '프로그램 성실 참여 및 출석률 요건 충족',
            '단기(5주)·중장기(15주 이상) 과정 선택',
        ],
        steps: ['운영기관 상담 신청', '참여 자격 확인', '맞춤형 프로그램 참여', '이수 후 인센티브 지급 및 취업 연계'],
        tags: ['청년도전지원사업', '구직단념청년', '청년지원', '고용노동부', '자립준비청년'],
        changes2026: '중장기 과정 이수 시 인센티브가 확대되어 실질적인 취업 연계 효과가 강화되었습니다.',
        outlook2027:
            '2027년에는 청년첫취업지원제도(월 65만원 구직촉진수당, 16만명 대상)가 신설되어 청년 구직 지원 체계가 대폭 확대됩니다.',
        updated: TODAY,
        views: 58900,
        link: 'https://www.work24.go.kr',
    },
    {
        id: 35,
        slug: 'chwieop-hu-sanghwan-daechul',
        category: 'education',
        title: '취업 후 상환 학자금대출(ICL)',
        summary: '재학 중에는 이자를 유예하고 취업 후 소득이 생기면 갚는 학자금 대출입니다.',
        description:
            '등록금과 생활비를 대출받고, 졸업 후 일정 기준 이상의 소득이 발생한 시점부터 소득에 비례해 상환하는 제도입니다. 소득이 없으면 상환 의무가 발생하지 않습니다.',
        eligibility: '학자금 지원구간 9구간 이하, 만 35세 이하 대학생(대학원생은 일반 상환 대출)',
        amount: '등록금 전액 + 생활비 학기당 최대 200만원',
        applicationPeriod: '학기별 신청 기간(1학기 1~4월, 2학기 6~9월)',
        applicationMethod: '한국장학재단 홈페이지',
        agency: '교육부 · 한국장학재단',
        requirements: [
            '학자금 지원구간 9구간 이하',
            '만 35세 이하(일부 예외 인정)',
            '직전 학기 성적 C학점(70점) 이상',
            '소득 발생 전까지 상환 유예',
            '기초·차상위 및 다자녀 가구는 재학 중 이자 면제',
        ],
        steps: ['한국장학재단에서 대출 신청', '가구원 정보제공 동의 및 심사', '대출 승인 및 약정 체결', '대학에 등록금 이체'],
        tags: ['학자금대출', '취업후상환', 'ICL', '한국장학재단', '등록금'],
        changes2026: '재학 중 이자 면제 대상이 확대되어 저소득층 학생의 이자 부담이 줄었습니다.',
        outlook2027: '2027년 지방 국립대 등록금 전액 장학금 도입으로 학자금 대출 수요가 일부 줄어들 전망입니다.',
        updated: TODAY,
        views: 88300,
        link: 'https://www.kosaf.go.kr',
    },

    /* ─────────────────────── 창업·소상공인 ─────────────────────── */
    {
        id: 41,
        slug: 'yebi-changeop-package',
        category: 'business',
        title: '예비창업패키지',
        summary: '예비창업자에게 최대 1억원의 사업화 자금과 창업 교육을 지원합니다.',
        description:
            '혁신적인 기술 창업 아이템을 보유한 예비창업자에게 사업화 자금, 창업교육, 전담 멘토링을 패키지로 지원하는 대표적인 정부 창업지원 사업입니다.',
        eligibility: '공고일 기준 사업자등록을 하지 않은 예비창업자',
        amount: '최대 1억원 (평균 약 5천만원)',
        applicationPeriod: '연 1~2회 (통상 2~3월 공고)',
        applicationMethod: 'K-스타트업 창업지원포털 온라인 접수',
        agency: '중소벤처기업부 · 창업진흥원',
        requirements: [
            '공고일 현재 사업자등록(개인·법인)을 하지 않은 자',
            '기술 기반 창업 아이템 보유',
            '선정 후 협약 기간 내 사업자등록 필수',
            '사업화 자금은 인건비·시제품 제작비·마케팅비 등에 사용',
            '창업교육 및 멘토링 의무 이수',
        ],
        documents: ['사업계획서(정부 양식)', '대표자 신분증', '증빙서류(특허·수상실적 등)'],
        steps: [
            'K-스타트업에서 공고 확인 및 온라인 접수',
            '요건 검토 및 서면평가',
            '발표평가(피칭)',
            '최종 선정 및 협약 체결',
            '사업자등록 후 사업화 자금 집행',
        ],
        faq: [
            {
                q: '나이 제한이 있나요?',
                a: '예비창업패키지는 일반분야와 특화분야로 나뉘며, 연령 제한은 공고별로 다릅니다. 청년 특화 트랙은 만 39세 이하가 대상입니다.',
            },
        ],
        tags: ['예비창업패키지', '창업지원금', 'K-스타트업', '창업진흥원', '사업화자금'],
        changes2026: 'AI·딥테크 분야 특화 트랙이 확대되어 기술 창업 지원 비중이 높아졌습니다.',
        outlook2027:
            '2027년 예산안에서 3대 메가프로젝트와 AI 투자가 10조 8천억원에서 21조 3천억원으로 두 배 가까이 늘어, 관련 분야 창업 지원도 확대될 전망입니다.',
        updated: TODAY,
        views: 132600,
        link: 'https://www.k-startup.go.kr',
    },
    {
        id: 42,
        slug: 'chogi-changeop-package',
        category: 'business',
        title: '초기창업패키지',
        summary: '창업 3년 이내 기업에 최대 1억원의 사업화 자금을 지원합니다.',
        description:
            '창업 3년 이내 초기 기업이 시장에 안착할 수 있도록 사업화 자금과 아이템 검증, 판로 개척 프로그램을 지원합니다.',
        eligibility: '공고일 기준 창업 3년 이내(업력 3년 미만) 기업',
        amount: '최대 1억원',
        applicationPeriod: '연 1~2회 (통상 2~3월 공고)',
        applicationMethod: 'K-스타트업 창업지원포털 온라인 접수',
        agency: '중소벤처기업부 · 창업진흥원',
        requirements: [
            '창업 3년 이내 기업(개인·법인)',
            '기술 기반 창업기업',
            '사업화 자금의 자기부담금 일부 부담',
            '주관기관 프로그램 참여 필수',
            '중복 지원 사업 확인 필요',
        ],
        steps: ['K-스타트업 공고 확인 및 접수', '서면·발표 평가', '선정 및 협약', '사업화 자금 집행 및 성과 보고'],
        tags: ['초기창업패키지', '창업 3년', 'K-스타트업', '사업화자금', '스타트업'],
        changes2026: '데이터·AI 활용 창업기업에 대한 가점이 확대되었습니다.',
        outlook2027: '2027년 미래 성장엔진 예산 62조 8천억원 투입으로 초기 스타트업 지원 규모가 늘어날 전망입니다.',
        updated: TODAY,
        views: 104300,
        link: 'https://www.k-startup.go.kr',
    },
    {
        id: 43,
        slug: 'sosanggongin-jeongchaek-jageum',
        category: 'business',
        title: '소상공인 정책자금',
        summary: '소상공인에게 시중은행보다 낮은 금리로 경영·창업 자금을 융자합니다.',
        description:
            '소상공인시장진흥공단이 운영하는 정책 융자로, 일반경영안정자금·성장기반자금·재도전특별자금 등 상황에 맞는 자금을 저금리로 빌릴 수 있습니다.',
        eligibility: '상시근로자 5인 미만(제조·건설·운수·광업은 10인 미만) 소상공인',
        amount: '자금 종류별 최대 7천만원~2억원',
        applicationPeriod: '연중 상시 (예산 소진 시 마감)',
        applicationMethod: '소상공인정책자금 누리집 온라인 신청',
        agency: '중소벤처기업부 · 소상공인시장진흥공단',
        requirements: [
            '소상공인 기본법상 소상공인 요건 충족',
            '사업자등록 후 일정 기간 경과(자금 종류별 상이)',
            '신용도 및 상환능력 심사 통과',
            '유흥업 등 융자 제외 업종이 아닐 것',
            '자금 종류별 교육 이수 요건 확인',
        ],
        documents: ['사업자등록증', '부가가치세 과세표준증명원', '임대차계약서', '재무제표 또는 소득금액증명원'],
        steps: [
            '소상공인정책자금 누리집에서 자금 유형 확인',
            '온라인 신청 및 서류 제출',
            '심사(직접대출) 또는 은행 연계(대리대출)',
            '대출 약정 및 실행',
        ],
        tags: ['소상공인정책자금', '소진공', '경영안정자금', '융자', '자영업'],
        changes2026: '고금리·내수 부진에 대응한 경영안정자금 공급이 확대되었습니다.',
        outlook2027: '2027년 양극화 대응 예산 확대와 함께 소상공인 금융 지원이 강화될 전망입니다.',
        updated: TODAY,
        views: 158700,
        link: 'https://ols.semas.or.kr',
    },
    {
        id: 44,
        slug: 'noran-usan-gongje',
        category: 'business',
        title: '노란우산공제',
        summary: '소상공인의 폐업 대비 목돈 마련과 연 최대 600만원 소득공제를 지원합니다.',
        description:
            '소기업·소상공인이 폐업·질병 등의 위기에 대비해 생활 안정과 재기를 준비할 수 있도록 돕는 공제 제도입니다. 납입 부금은 법적으로 압류가 금지되고 소득공제 혜택이 큽니다.',
        eligibility: '사업자등록을 한 소기업·소상공인 대표자',
        amount: '납입액 기준 연 200만~600만원 소득공제 + 복리 이자',
        applicationPeriod: '연중 상시',
        applicationMethod: '중소기업중앙회 노란우산 홈페이지 또는 은행 창구',
        agency: '중소벤처기업부 · 중소기업중앙회',
        requirements: [
            '소기업·소상공인 범위에 해당하는 사업자 대표',
            '월 5만원~100만원 범위에서 부금 납입',
            '폐업·사망·퇴임·노령 시 공제금 지급',
            '납입 부금은 압류·양도·담보 제공 금지',
            '사업소득 규모에 따라 소득공제 한도 차등',
        ],
        steps: ['노란우산 홈페이지 또는 은행에서 가입 신청', '사업자 확인 및 부금액 결정', '매월 자동이체 납입', '폐업 등 공제 사유 발생 시 청구'],
        tags: ['노란우산공제', '소상공인', '소득공제', '폐업대비', '중소기업중앙회'],
        changes2026: '지자체별 희망장려금(가입 장려금) 지원이 확대되고 있습니다.',
        outlook2027: '2027년에도 소상공인 사회안전망 강화 차원에서 가입 지원이 이어질 전망입니다.',
        updated: TODAY,
        views: 92400,
        link: 'https://www.8899.or.kr',
    },
    {
        id: 45,
        slug: 'huimang-return-package',
        category: 'business',
        title: '희망리턴패키지(재취업·재창업 지원)',
        summary: '폐업(예정) 소상공인의 재기를 위해 점포철거비와 재취업 교육을 지원합니다.',
        description:
            '경영 위기에 처했거나 폐업을 준비하는 소상공인에게 사업정리 컨설팅, 점포철거비, 재취업·재창업 교육과 수당을 지원하는 재기 지원 사업입니다.',
        eligibility: '폐업 예정이거나 폐업한 지 일정 기간 이내인 소상공인',
        amount: '점포철거비 최대 400만원 · 재취업 장려수당 최대 100만원 · 재창업 사업화 자금 최대 2천만원',
        applicationPeriod: '연중 상시 (예산 소진 시 마감)',
        applicationMethod: '소상공인마당 또는 희망리턴패키지 누리집 신청',
        agency: '중소벤처기업부 · 소상공인시장진흥공단',
        requirements: [
            '사업자등록을 하고 실제 영업한 소상공인',
            '폐업 예정 또는 폐업 신고 완료',
            '점포철거비는 실제 철거 후 증빙 제출 시 지급',
            '재취업 교육 이수 및 취업 시 장려수당 지급',
            '재창업 과정은 업종 전환 계획 필요',
        ],
        steps: ['희망리턴패키지 누리집에서 신청', '사업정리 컨설팅 진행', '점포철거 후 비용 청구', '재취업 또는 재창업 프로그램 참여'],
        tags: ['희망리턴패키지', '폐업지원', '점포철거비', '재창업', '소상공인'],
        changes2026: '내수 부진에 대응해 점포철거비 지원 규모와 대상이 확대되었습니다.',
        outlook2027: '2027년에도 소상공인 재기 지원 예산이 지속 확대될 것으로 보입니다.',
        updated: TODAY,
        views: 71200,
        link: 'https://www.sbiz.or.kr',
    },

    /* ─────────────────────── 복지·취약계층 ─────────────────────── */
    {
        id: 51,
        slug: 'saenggye-geubyeo',
        category: 'welfare',
        title: '생계급여(기초생활보장)',
        summary: '2026년 4인 가구 기준 월 최대 207만 8,316원까지 생계비를 지원합니다.',
        description:
            '소득인정액이 기준 중위소득 32% 이하인 가구에 최저 생활을 보장하기 위해 매월 현금으로 지급하는 기초생활보장제도의 핵심 급여입니다. 선정기준이 곧 최저보장수준이며, 실제로는 기준액에서 가구의 소득인정액을 뺀 금액을 받습니다.',
        eligibility: '소득인정액이 2026년 기준 중위소득 32% 이하인 가구',
        amount: '1인 가구 월 820,556원 · 4인 가구 월 2,078,316원 (소득인정액 차감 후 지급)',
        applicationPeriod: '연중 상시',
        applicationMethod: '거주지 주민센터 방문 또는 복지로 온라인 신청',
        agency: '보건복지부',
        requirements: [
            '2026년 기준 중위소득 32% 이하 (1인 820,556원 / 4인 2,078,316원)',
            '소득인정액 = 소득평가액 + 재산의 소득환산액',
            '부양의무자 기준은 원칙적으로 폐지(고소득·고재산 부양의무자만 예외 적용)',
            '근로능력이 있는 수급자는 자활사업 참여 조건 부과 가능',
            '실제 지급액은 기준액에서 소득인정액을 뺀 차액',
        ],
        documents: [
            '사회보장급여 신청서',
            '금융정보 등 제공 동의서',
            '소득·재산 신고서',
            '임대차계약서(해당 시)',
            '신분증',
        ],
        steps: [
            '주민센터 방문 또는 복지로에서 신청',
            '소득·재산 및 금융 조사(약 30일)',
            '부양의무자 조사(해당 시)',
            '보장 결정 및 통지',
            '매월 20일 계좌로 지급',
        ],
        faq: [
            {
                q: '자녀가 소득이 있으면 못 받나요?',
                a: '생계급여의 부양의무자 기준은 원칙적으로 폐지되었습니다. 다만 부양의무자의 연소득이 1억원을 넘거나 일반재산이 9억원을 초과하면 예외적으로 적용됩니다.',
            },
            {
                q: '얼마를 받게 되나요?',
                a: '선정기준액에서 가구의 소득인정액을 뺀 금액을 받습니다. 소득이 전혀 없는 4인 가구라면 월 2,078,316원 전액을 받습니다.',
            },
        ],
        tags: ['생계급여', '기초생활수급', '기준중위소득', '복지로', '저소득'],
        changes2026:
            '2026년 기준 중위소득이 6.51% 인상(역대 최대)되면서 4인 가구 생계급여 최대 지급액이 월 207만 8,316원으로 약 12만 7천원 올랐습니다.',
        outlook2027:
            '2027년에는 기준 중위소득이 6.7% 인상되어 다시 한 번 역대 최대 폭으로 오르고, 의료급여 부양의무자 기준이 폐지되어 수급권자가 확대됩니다.',
        updated: TODAY,
        views: 356800,
        link: 'https://www.bokjiro.go.kr',
        hot: true,
    },
    {
        id: 52,
        slug: 'uiryo-geubyeo',
        category: 'welfare',
        title: '의료급여',
        summary: '기준 중위소득 40% 이하 가구의 병원비를 국가가 대부분 부담합니다.',
        description:
            '생활이 어려운 국민이 의료비 부담 없이 진료를 받을 수 있도록 진찰·검사·치료·입원 등 의료 서비스 비용을 지원하는 제도입니다. 1종과 2종으로 나뉘며 본인부담금이 매우 낮습니다.',
        eligibility: '소득인정액이 2026년 기준 중위소득 40% 이하인 가구',
        amount: '외래 1,000~2,000원 수준의 본인부담금만 부담 (1종 기준)',
        applicationPeriod: '연중 상시',
        applicationMethod: '거주지 주민센터 방문 또는 복지로 온라인 신청',
        agency: '보건복지부',
        requirements: [
            '2026년 기준 중위소득 40% 이하(4인 가구 월 2,597,895원 이하)',
            '1종: 근로무능력가구, 희귀난치성질환자, 중증질환자 등',
            '2종: 1종에 해당하지 않는 의료급여 수급권자',
            '부양의무자 기준 적용(2027년 폐지 예정)',
            '의료급여증 또는 신분증으로 병원 이용',
        ],
        documents: ['사회보장급여 신청서', '금융정보 제공 동의서', '진단서(중증질환자 등)', '신분증'],
        steps: ['주민센터 또는 복지로 신청', '소득·재산 조사', '의료급여 종별 결정', '의료기관 이용 시 자격 자동 적용'],
        tags: ['의료급여', '병원비지원', '기초생활보장', '1종2종', '본인부담금'],
        changes2026: '2026년 기준 중위소득 인상으로 선정기준액이 4인 가구 월 259만 7,895원으로 올랐습니다.',
        outlook2027:
            '2027년 예산안에서 의료급여 부양의무자 기준을 폐지하기로 해, 자녀 소득 때문에 탈락했던 저소득 노인·환자들이 새로 수급권을 얻게 됩니다.',
        updated: TODAY,
        views: 143900,
        link: 'https://www.bokjiro.go.kr',
    },
    {
        id: 53,
        slug: 'gicho-yeongeum',
        category: 'welfare',
        title: '기초연금',
        summary: '만 65세 이상 소득 하위 70% 어르신에게 월 최대 349,700원을 지급합니다.',
        description:
            '만 65세 이상 어르신 중 소득인정액이 선정기준액 이하인 분에게 매월 지급하는 공적 연금입니다. 2026년에는 소비자물가상승률을 반영해 지급액이 인상되고 선정기준액도 크게 올라 수급 대상이 확대되었습니다.',
        eligibility: '만 65세 이상, 소득인정액이 단독가구 월 247만원·부부가구 월 395만 2천원 이하',
        amount: '단독가구 월 최대 349,700원 · 부부가구 월 최대 559,520원(20% 감액 적용)',
        applicationPeriod: '만 65세 생일이 속한 달의 1개월 전부터 신청 가능 (연중 상시)',
        applicationMethod: '주민센터, 국민연금공단 지사 방문 또는 복지로 온라인 신청',
        agency: '보건복지부 · 국민연금공단',
        requirements: [
            '만 65세 이상 대한민국 국적자로 국내 거주',
            '소득인정액이 선정기준액 이하(단독 247만원 / 부부 395만 2천원)',
            '부부가 모두 받으면 각각 20% 감액',
            '국민연금 수령액이 많으면 일부 감액될 수 있음',
            '공무원연금 등 직역연금 수급자는 원칙적으로 제외',
        ],
        documents: ['기초연금 지급 신청서', '신분증', '통장 사본', '금융정보 제공 동의서', '전월세 계약서(해당 시)'],
        steps: [
            '만 65세 생일 1개월 전부터 신청 가능',
            '주민센터·국민연금공단 방문 또는 복지로 신청',
            '소득·재산 조사',
            '수급 결정 통지',
            '매월 25일 지급',
        ],
        faq: [
            {
                q: '국민연금을 받으면 기초연금을 못 받나요?',
                a: '받을 수 있습니다. 다만 국민연금 수령액이 기초연금 기준연금액의 150%를 넘으면 기초연금이 일부 감액될 수 있습니다.',
            },
            {
                q: '부부가 함께 받으면 얼마인가요?',
                a: '부부가 모두 수급하면 각각 20% 감액되어 부부 합산 월 최대 559,520원을 받습니다.',
            },
        ],
        tags: ['기초연금', '노인복지', '만65세', '국민연금공단', '노령연금'],
        changes2026:
            '2026년 기초연금 선정기준액이 단독가구 228만원에서 247만원으로, 부부가구는 364만 8천원에서 395만 2천원으로 인상되어 수급자가 약 43만명 늘어날 것으로 예상됩니다. 지급액은 물가상승률 2.1%를 반영해 단독가구 월 최대 349,700원입니다.',
        outlook2027:
            '2027년 기초연금 예산은 23조 1천억원에서 25조 7천억원으로 약 11% 늘어납니다. 다만 소득 하위 70% 기준을 유지하기로 하면서 지급 구조 개편은 추후 과제로 남았습니다.',
        updated: TODAY,
        views: 421500,
        link: 'https://basicpension.mohw.go.kr',
        hot: true,
    },
    {
        id: 54,
        slug: 'gingeup-bokji-jiwon',
        category: 'welfare',
        title: '긴급복지지원',
        summary: '갑작스러운 위기 상황에 처한 가구에 생계비·의료비를 신속하게 지원합니다.',
        description:
            '주소득자의 사망·실직, 중한 질병, 화재 등으로 갑자기 생계 유지가 곤란해진 가구에 대해 선지원 후조사 원칙으로 신속하게 생계비, 의료비, 주거비 등을 지원하는 제도입니다.',
        eligibility: '위기사유가 발생하고 소득·재산 기준을 충족하는 가구',
        amount: '생계지원 가구원수별 차등 지급 · 의료지원 회당 최대 300만원 · 주거지원 등',
        applicationPeriod: '위기상황 발생 시 즉시 (연중 상시)',
        applicationMethod: '시·군·구청, 주민센터 또는 보건복지상담센터(129) 전화',
        agency: '보건복지부',
        requirements: [
            '주소득자 사망·가출·행방불명·구금',
            '중한 질병 또는 부상',
            '화재·자연재해로 거주지에서 생활이 곤란',
            '가정폭력·성폭력 피해, 실직, 휴폐업 등',
            '소득 기준 중위소득 75% 이하, 금융재산 기준 충족',
        ],
        documents: ['긴급지원 신청서', '위기상황 증빙자료(사망진단서, 진단서, 해고통지서 등)', '신분증', '통장 사본'],
        steps: [
            '129 전화 또는 주민센터 방문 신청',
            '현장 확인 후 선지원 결정(1~2일 내)',
            '생계비·의료비 등 지원',
            '사후 소득·재산 조사',
            '필요 시 기초생활보장 등 연계',
        ],
        faq: [
            {
                q: '조사 전에 먼저 받을 수 있나요?',
                a: '긴급복지지원은 선지원 후조사 원칙이 적용되어, 위기 상황이 확인되면 먼저 지원하고 이후에 소득·재산을 조사합니다.',
            },
        ],
        tags: ['긴급복지지원', '위기가구', '129', '생계비', '선지원후조사'],
        changes2026: '2026년 기준 중위소득 인상에 따라 생계지원 금액과 소득 기준이 함께 올랐습니다.',
        outlook2027: '2027년 사회안전망 강화 기조에 따라 위기가구 발굴과 지원 규모가 확대될 전망입니다.',
        updated: TODAY,
        views: 134200,
        link: 'https://www.bokjiro.go.kr',
    },
    {
        id: 55,
        slug: 'energy-voucher',
        category: 'welfare',
        title: '에너지바우처',
        summary: '취약계층에 연간 최대 70만원의 냉난방 에너지 비용을 지원합니다.',
        description:
            '전기·도시가스·지역난방·등유·LPG·연탄 등 에너지 구입 비용을 지원하는 바우처입니다. 2026년부터는 하절기·동절기 구분 없이 연간 총액을 자유롭게 사용할 수 있도록 개편되었습니다.',
        eligibility: '생계·의료·주거·교육급여 수급자 중 노인·영유아·장애인·임산부 등 더위·추위 민감계층 포함 가구',
        amount: '1인 세대 295,200원 ~ 4인 이상 세대 701,300원 (연간 총액)',
        applicationPeriod: '2026년 6월 15일부터 신청 (사용기한 2027년 5월 31일까지)',
        applicationMethod: '주민센터 방문 신청 또는 복지로 온라인 신청',
        agency: '산업통상자원부 · 한국에너지공단',
        requirements: [
            '국민기초생활보장법상 생계·의료·주거·교육급여 수급자',
            '세대원 중 노인(만 65세 이상), 영유아(만 7세 미만), 장애인, 임산부, 중증질환자, 한부모가족, 소년소녀가정 포함',
            '세대원 수에 따라 지원 금액 차등',
            '전기·가스 요금 자동 차감 또는 국민행복카드 결제',
            '사용기한 내 미사용 잔액은 소멸',
        ],
        documents: ['에너지이용권 신청서', '신분증', '(해당 시) 세대원 자격 증빙서류'],
        steps: [
            '6월 15일부터 주민센터 또는 복지로에서 신청',
            '자격 확인',
            '요금차감 또는 국민행복카드 방식 선택',
            '2027년 5월 31일까지 사용',
        ],
        faq: [
            {
                q: '2026년에 무엇이 바뀌었나요?',
                a: '기존에는 하절기·동절기 바우처가 분리되어 있었지만, 2026년부터는 계절 구분 없이 연간 총액을 필요할 때 자유롭게 사용할 수 있습니다.',
            },
        ],
        tags: ['에너지바우처', '냉난방비', '전기요금', '취약계층', '한국에너지공단'],
        changes2026:
            '하절기·동절기 구분이 폐지되고 연간 총액을 통합 사용하는 방식으로 개편되었으며, 지원 금액은 1인 세대 295,200원부터 4인 이상 세대 701,300원까지입니다.',
        outlook2027: '2027년에도 에너지 취약계층 보호 기조가 유지되며 지원 단가 인상이 검토되고 있습니다.',
        updated: TODAY,
        views: 186500,
        link: 'https://www.energyv.or.kr',
        isNew: true,
    },
    {
        id: 56,
        slug: 'tongsinbi-gammyeon',
        category: 'welfare',
        title: '통신요금 감면',
        summary: '취약계층에 이동전화 요금을 월 최대 2만 6천원까지 감면합니다.',
        description:
            '기초생활수급자, 차상위계층, 장애인, 국가유공자, 만 65세 이상 기초연금 수급자 등에게 이동전화와 인터넷 요금을 감면해 주는 제도입니다. 별도 신청이 필요하며 소급 적용은 되지 않습니다.',
        eligibility: '기초생활수급자, 차상위계층, 장애인, 국가유공자, 기초연금 수급자 등',
        amount: '기초생활수급자 월 최대 26,000원 · 차상위계층 월 최대 21,500원 · 기초연금 수급자 요금의 50%',
        applicationPeriod: '연중 상시',
        applicationMethod: '통신사 대리점, 고객센터(114), 정부24 또는 복지로 온라인 신청',
        agency: '과학기술정보통신부',
        requirements: [
            '기초생활수급자(생계·의료·주거·교육급여)',
            '차상위계층',
            '장애인, 국가유공자, 기초연금 수급자',
            '본인 명의 회선에 한해 감면 적용',
            '가구당 감면 회선 수 제한 있음',
        ],
        steps: ['정부24·복지로에서 온라인 신청 또는 통신사 대리점 방문', '자격 확인', '다음 청구분부터 요금 감면 적용'],
        faq: [
            {
                q: '신청하지 않아도 자동으로 감면되나요?',
                a: '아닙니다. 반드시 별도로 신청해야 하며, 신청 이전 기간에 대한 소급 감면은 되지 않습니다.',
            },
        ],
        tags: ['통신비감면', '요금감면', '취약계층', '기초연금', '정부24'],
        changes2026: '온라인 신청 채널이 확대되어 정부24와 복지로에서 간편하게 신청할 수 있습니다.',
        outlook2027: '2027년에도 통신비 부담 경감 정책이 유지될 전망입니다.',
        updated: TODAY,
        views: 112700,
        link: 'https://www.bokjiro.go.kr',
    },
    {
        id: 57,
        slug: 'chasangwi-gyecheung',
        category: 'welfare',
        title: '차상위계층 지원',
        summary: '기초수급자 바로 위 소득층에 의료·교육·통신·문화 혜택을 제공합니다.',
        description:
            '기초생활수급자는 아니지만 소득인정액이 기준 중위소득 50% 이하인 가구를 차상위계층으로 인정해 각종 감면과 지원을 제공하는 제도입니다. 차상위 확인서를 발급받으면 다양한 혜택에 연계됩니다.',
        eligibility: '소득인정액이 2026년 기준 중위소득 50% 이하이면서 기초생활수급자가 아닌 가구',
        amount: '의료비 본인부담 경감, 통신비·전기요금 감면, 문화누리카드 등 항목별 지원',
        applicationPeriod: '연중 상시',
        applicationMethod: '주민센터 방문 또는 복지로 온라인 신청',
        agency: '보건복지부',
        requirements: [
            '2026년 기준 중위소득 50% 이하(4인 가구 월 3,247,369원 이하)',
            '기초생활보장 수급자가 아닐 것',
            '재산의 소득환산액 포함 소득인정액 기준 충족',
            '차상위 본인부담경감, 자활사업, 장애수당 등 유형별 신청',
            '차상위 확인서 발급 후 각종 감면 연계',
        ],
        documents: ['사회보장급여 신청서', '금융정보 제공 동의서', '소득·재산 신고서', '신분증'],
        steps: ['주민센터 또는 복지로에서 신청', '소득·재산 조사', '차상위계층 확인 및 통지', '각 지원 항목별 혜택 신청'],
        tags: ['차상위계층', '중위소득50%', '본인부담경감', '문화누리카드', '복지로'],
        changes2026: '2026년 기준 중위소득 6.51% 인상으로 차상위 인정 기준이 완화되었습니다.',
        outlook2027: '2027년 기준 중위소득 6.7% 인상으로 차상위 대상이 더 넓어질 예정입니다.',
        updated: TODAY,
        views: 98600,
        link: 'https://www.bokjiro.go.kr',
    },
    {
        id: 58,
        slug: 'noin-iljari',
        category: 'welfare',
        title: '노인일자리 및 사회활동 지원사업',
        summary: '만 65세 이상 어르신에게 일자리와 월 활동비를 지원합니다.',
        description:
            '어르신이 활기찬 노후를 보내고 소득을 보충할 수 있도록 공익활동, 사회서비스형, 시장형 등 다양한 유형의 일자리를 제공하는 사업입니다.',
        eligibility: '만 65세 이상(일부 유형은 만 60세 이상) 어르신',
        amount: '공익활동형 월 29만원 수준 · 사회서비스형 월 76만원 수준 (유형별 상이)',
        applicationPeriod: '통상 12월~다음 해 1월 모집 (연중 수시 추가 모집)',
        applicationMethod: '주민센터, 노인복지관, 시니어클럽 방문 또는 노인일자리여기 누리집',
        agency: '보건복지부 · 한국노인인력개발원',
        requirements: [
            '공익활동형: 만 65세 이상 기초연금 수급자',
            '사회서비스형: 만 65세 이상(일부 만 60세 이상)',
            '시장형 사업단: 만 60세 이상',
            '국민기초생활보장 생계급여 수급자는 공익활동형 참여 제한',
            '유형별 활동 시간과 활동비 상이',
        ],
        steps: ['노인일자리여기 또는 주민센터에서 모집 공고 확인', '신청서 접수', '선발 심사', '배치 및 활동 시작', '월별 활동비 지급'],
        tags: ['노인일자리', '공익활동', '시니어', '기초연금', '노후소득'],
        changes2026: '노인일자리 규모가 지속 확대되고 사회서비스형 비중이 늘고 있습니다.',
        outlook2027: '2027년 예산안에서 노인일자리를 118만개로 확대하기로 하여 역대 최대 규모가 됩니다.',
        updated: TODAY,
        views: 176900,
        link: 'https://www.seniorro.or.kr',
    },
];

/* ─────────────────────────── 헬퍼 ─────────────────────────── */

export const getProgramsByCategory = (category: string): SupportProgram[] =>
    category === 'all' ? supportPrograms : supportPrograms.filter(p => p.category === category);

export const searchPrograms = (searchTerm: string, category: string = 'all'): SupportProgram[] => {
    const programs = getProgramsByCategory(category);
    const term = searchTerm.trim().toLowerCase();
    if (!term) return programs;

    return programs.filter(p =>
        [p.title, p.summary, p.description, p.eligibility, p.amount, p.agency, ...p.tags]
            .join(' ')
            .toLowerCase()
            .includes(term)
    );
};

export const getProgramBySlug = (slug: string): SupportProgram | undefined =>
    supportPrograms.find(p => p.slug === slug);

/** 조회수 기준 인기 지원금 */
export const getPopularPrograms = (limit = 10, category: string = 'all'): SupportProgram[] =>
    [...getProgramsByCategory(category)].sort((a, b) => b.views - a.views).slice(0, limit);

/** 2026년에 달라진 내용이 있는 지원금 */
export const getUpdatedPrograms = (limit = 6): SupportProgram[] =>
    supportPrograms.filter(p => p.isNew || p.hot).slice(0, limit);

/** 같은 카테고리의 관련 지원금 */
export const getRelatedPrograms = (program: SupportProgram, limit = 4): SupportProgram[] =>
    supportPrograms
        .filter(p => p.category === program.category && p.id !== program.id)
        .sort((a, b) => b.views - a.views)
        .slice(0, limit);

/** 전체 태그를 인기순으로 */
export const getPopularTags = (limit = 18): string[] => {
    const counts: Record<string, number> = {};
    supportPrograms.forEach(p => {
        p.tags.forEach(t => {
            counts[t] = (counts[t] ?? 0) + p.views;
        });
    });
    return Object.keys(counts)
        .sort((a, b) => counts[b] - counts[a])
        .slice(0, limit);
};
